import { FC, useCallback, useRef } from 'react';
import { AlgorithmGraphActions } from '../AlgorithmGraphActions';
import type { SourceDimensionsMap } from '../joins/useAlgorithmJoinsDataQuery';
import { AlgorithmMappingsFormModal } from '../mappings/AlgorithmMappingsFormModal';
import { AlgorithmJoinsWithMappingsGraphFormModal } from './graph/AlgorithmJoinsWithMappingsGraphFormModal';
import type { AlgorithmMappingQueryDtoType } from '../mappings/schema/types';
import { AlgorithmSourceFormDataType } from '../sources/schema/types';
import { AlgorithmJoinsFormModal } from '../joins/AlgorithmJoinsFormModal';
import { AlgorithmJoinFormDataType } from '../joins/schema/types';
import { confirmPanelFormClose, type FormRef } from '../../../common/form';
import { useWatch } from 'react-hook-form';
import { AlgorithmType } from '../schema/zAlgorithmType';

export interface AlgorithmJoinsAndMappingsEditModalProps {
  isModalOpen: 'joins' | 'mappings' | 'graph' | null;
  algorithmId: number;
  joins: AlgorithmJoinFormDataType[];
  sources: AlgorithmSourceFormDataType[];
  sourceDimensionsMap: SourceDimensionsMap;
  mappings: AlgorithmMappingQueryDtoType[];
  onClose?: () => void;
  onChange?: (changedTypes: ('joins' | 'mappings')[]) => void;
  onSwitchModalType?: (
    modalType: 'joins' | 'mappings' | 'graph' | null,
  ) => void;
}

export const AlgorithmJoinsAndMappingsEditModal: FC<
  AlgorithmJoinsAndMappingsEditModalProps
> = (props) => {
  const {
    isModalOpen,
    algorithmId,
    joins,
    sources,
    sourceDimensionsMap,
    mappings,
    onClose,
    onChange,
    onSwitchModalType,
  } = props;

  const transitionRef = useRef(true);
  const formRef = useRef<FormRef<any>>(undefined!);
  const typeCode = useWatch({ name: 'typeCode' });

  const handleModalTypeChange = useCallback(
    async (modalType: 'joins' | 'mappings' | 'graph') => {
      transitionRef.current = false;
      const restoreTransition = () => {
        transitionRef.current = true;
      };
      const doSwitch = () => {
        onSwitchModalType?.(modalType);
        setTimeout(restoreTransition, 500);
      };

      if (formRef.current?.isBlocked) {
        confirmPanelFormClose(doSwitch, restoreTransition);
      } else {
        doSwitch();
      }
    },
    [onSwitchModalType],
  );

  if (!isModalOpen) {
    return null;
  }

  const actions = (
    <AlgorithmGraphActions
      algorithmType={typeCode}
      value={isModalOpen}
      onChange={handleModalTypeChange}
    />
  );

  if (isModalOpen === 'mappings') {
    return (
      <AlgorithmMappingsFormModal
        formRef={formRef}
        actions={actions}
        algorithmId={algorithmId}
        sources={sources}
        sourceDimensionsMap={sourceDimensionsMap}
        mappings={mappings}
        onClose={onClose}
        onChange={() => onChange?.(['mappings'])}
        transition={transitionRef.current}
      />
    );
  } else if (isModalOpen === 'joins' && typeCode === AlgorithmType.INSERT) {
    return (
      <AlgorithmJoinsFormModal
        formRef={formRef}
        actions={actions}
        algorithmId={algorithmId}
        joins={joins}
        sources={sources}
        mappings={mappings}
        onClose={onClose}
        onChange={() => onChange?.(['joins'])}
        transition={transitionRef.current}
      />
    );
  }

  return (
    <AlgorithmJoinsWithMappingsGraphFormModal
      formRef={formRef}
      actions={actions}
      algorithmId={algorithmId}
      joins={joins}
      sources={sources}
      sourceDimensionsMap={sourceDimensionsMap}
      mappings={mappings}
      onClose={onClose}
      onChange={() => onChange?.(['mappings', 'joins'])}
      transition={transitionRef.current}
    />
  );
};
